<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatUsdFromCents } from '../helpers/money';
import { formatBillingDateTime } from '../helpers/formatBillingTime';

const props = defineProps({
  subscription: { type: Object, default: null },
  seats: { type: Object, default: null },
  catalog: { type: Object, default: null },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['upgrade', 'renew']);

const { t } = useI18n();

const isPro = computed(
  () => String(props.subscription?.plan_code || '').toLowerCase() === 'pro'
);

const planLabel = computed(() =>
  isPro.value ? t('CUSTOM.BILLING.PLAN.PRO') : t('CUSTOM.BILLING.PLAN.FREE')
);

const periodFee = computed(() => {
  if (!isPro.value) return 0;
  const base = Number(props.catalog?.pro_price_cents) || 0;
  const addonSeats = Math.max(
    0,
    (Number(props.seats?.limit) || 0) - (Number(props.catalog?.base_seats) || 5)
  );
  const seatPrice = Number(props.catalog?.seat_price_cents) || 0;
  return base + addonSeats * seatPrice;
});

const membersLabel = computed(() => {
  const used = props.seats?.used ?? '—';
  const limit = props.seats?.limit ?? props.subscription?.seat_limit ?? '—';
  return t('CUSTOM.BILLING.PLAN.MEMBERS', { used, limit });
});

const endsAtLabel = computed(() => {
  const ends = props.subscription?.current_period_ends_at;
  if (!ends) return t('CUSTOM.BILLING.PLAN.NO_EXPIRY');
  return formatBillingDateTime(ends);
});
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.PLAN.TITLE')"
    :description="t('CUSTOM.BILLING.PLAN.DESCRIPTION')"
  >
    <template #action>
      <div class="flex flex-wrap gap-2 justify-end">
        <Button
          v-if="!isPro"
          size="sm"
          color="blue"
          :label="t('CUSTOM.BILLING.PLAN.UPGRADE')"
          :is-loading="submitting"
          :disabled="submitting"
          @click="emit('upgrade')"
        />
        <Button
          v-else
          size="sm"
          color="slate"
          variant="outline"
          :label="t('CUSTOM.BILLING.PLAN.RENEW')"
          :is-loading="submitting"
          :disabled="submitting"
          @click="emit('renew')"
        />
      </div>
    </template>

    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 m-0">
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <dt class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.PLAN.NAME') }}
        </dt>
        <dd class="mb-0 mt-1 text-base font-semibold text-n-slate-12">
          {{ planLabel }}
        </dd>
      </div>
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <dt class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.PLAN.PERIOD_FEE') }}
        </dt>
        <dd class="mb-0 mt-1 text-base font-semibold text-n-slate-12">
          {{ formatUsdFromCents(periodFee) }}
        </dd>
      </div>
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <dt class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.PLAN.MEMBERS_LABEL') }}
        </dt>
        <dd class="mb-0 mt-1 text-sm font-medium text-n-slate-12">
          {{ membersLabel }}
        </dd>
      </div>
      <div class="rounded-lg bg-n-alpha-1 p-3">
        <dt class="text-xs text-n-slate-11">
          {{ t('CUSTOM.BILLING.PLAN.EXPIRES') }}
        </dt>
        <dd class="mb-0 mt-1 text-sm font-medium text-n-slate-12">
          {{ endsAtLabel }}
        </dd>
      </div>
    </dl>
  </BillingSectionCard>
</template>
